import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Database, Loader2, CheckCircle2, AlertTriangle, Lock, Upload } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 사용자가 업로드해둔 cleaned JSON URL (5,098명 정규화 데이터)
const DEFAULT_SOURCE_URL =
  "https://customer-assets.emergentagent.com/job_4dd1c192-09fc-47c7-827a-8651633b59ad/artifacts/b7k8s3sw_members_clean.json";

export default function AdminLegacyImportPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();

  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [sourceUrl, setSourceUrl] = useState(DEFAULT_SOURCE_URL);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || user?.role !== "admin") {
      navigate("/");
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // 봉인 상태 확인
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/legacy-import/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setStatus(await res.json());
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingStatus(false);
      }
    })();
  }, [token]);

  const handleRun = async () => {
    if (confirmText !== "IMPORT") {
      toast.error('확인란에 정확히 "IMPORT" 라고 입력해주세요');
      return;
    }
    if (!sourceUrl.startsWith("https://")) {
      toast.error("HTTPS URL만 허용됩니다");
      return;
    }

    setRunning(true);
    setResult(null);

    try {
      const form = new FormData();
      form.append("confirm_token", "IMPORT_LIZTV_LEGACY_2026");
      form.append("file_url", sourceUrl);

      const res = await fetch(`${API_URL}/api/admin/legacy-import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "임포트 실패");

      setResult(d);
      toast.success(`✅ ${d.inserted}명 임포트 완료`);

      // 봉인 상태 갱신
      const sres = await fetch(`${API_URL}/api/admin/legacy-import/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (sres.ok) setStatus(await sres.json());
    } catch (e) {
      toast.error(e.message);
      setResult({ error: e.message });
    } finally {
      setRunning(false);
    }
  };

  const sealed = status?.sealed === true;
  const previouslyImported = status?.imported_users_in_db || 0;

  if (authLoading || loadingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-burgundy-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-20 pb-12">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/admin")} className="p-2 rounded-lg hover:bg-stone-100">
            <ArrowLeft className="w-5 h-5 text-stone-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
              <Database className="w-6 h-6 text-burgundy-600" />
              레거시 회원 일괄 임포트
            </h1>
            <p className="text-sm text-stone-500">LizTV 5,098명 → newcoupling.com 회원 DB 일회성 마이그레이션</p>
          </div>
        </div>

        {/* 봉인 상태 카드 */}
        <div className={`rounded-2xl border p-5 mb-6 ${sealed ? "bg-emerald-50 border-emerald-200" : "bg-white border-stone-200"}`}>
          <div className="flex items-start gap-3">
            {sealed ? (
              <Lock className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
            )}
            <div className="flex-1">
              <h2 className="font-bold text-stone-800">
                {sealed ? "🔒 봉인됨 — 임포트 완료" : "⚠️ 미완료 — 1회 실행 가능"}
              </h2>
              {sealed && status?.flag && (
                <div className="mt-2 text-sm text-stone-600 space-y-1">
                  <p>완료 시각: <span className="font-mono">{status.flag.completed_at}</span></p>
                  <p>임포트 건수: <span className="font-bold text-emerald-700">{status.flag.imported_count?.toLocaleString()}명</span></p>
                  <p>실행자: {status.flag.completed_by}</p>
                  <p>현재 DB내 임포트 회원: <span className="font-bold">{previouslyImported.toLocaleString()}명</span></p>
                </div>
              )}
              {!sealed && (
                <p className="mt-1 text-sm text-stone-600">
                  엔드포인트는 1회 성공 후 자동 봉인됩니다. 실행 전 신중하게 확인하세요.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* 실행 폼 */}
        {!sealed && !result && (
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <h2 className="font-bold text-stone-800 mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-burgundy-600" />
              임포트 실행
            </h2>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-1 block">
                  소스 JSON URL
                </label>
                <input
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-300 font-mono"
                  data-testid="source-url"
                />
                <p className="text-[11px] text-stone-400 mt-1">
                  HTTPS URL — 기본값은 정규화된 5,098명 데이터 JSON입니다.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 space-y-1">
                <p className="font-semibold">⚠️ 실행 전 확인사항</p>
                <ul className="list-disc list-inside text-[13px] space-y-0.5">
                  <li>이메일/legacy_id 중복은 자동 스킵 (덮어쓰기 X)</li>
                  <li>admin@newcoupling.com 등 보호 계정은 절대 변경 안 됨</li>
                  <li>모든 임포트 회원은 비밀번호 재설정 필요 상태로 시작</li>
                  <li>실행 후 엔드포인트가 자동 봉인되어 재실행 불가</li>
                </ul>
              </div>

              <div>
                <label className="text-sm font-semibold text-stone-700 mb-1 block">
                  실행 확인 — 정확히 <code className="bg-stone-100 px-1.5 py-0.5 rounded">IMPORT</code> 입력
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder='"IMPORT" 입력'
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-300"
                  data-testid="confirm-input"
                />
              </div>

              <Button
                onClick={handleRun}
                disabled={running || confirmText !== "IMPORT"}
                className="w-full bg-burgundy-600 hover:bg-burgundy-700 text-white disabled:opacity-40"
                data-testid="run-import-btn"
              >
                {running ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> 임포트 진행 중... (1-2분 소요)</>
                ) : (
                  <><Database className="w-4 h-4 mr-2" /> 임포트 실행</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* 결과 카드 */}
        {result && (
          <div className={`rounded-2xl border p-6 ${result.error ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
            {result.error ? (
              <>
                <h2 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> 임포트 실패
                </h2>
                <p className="text-sm text-red-600 font-mono">{result.error}</p>
              </>
            ) : (
              <>
                <h2 className="font-bold text-emerald-700 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> 임포트 완료
                </h2>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Stat label="원본 레코드" value={result.source_records?.toLocaleString()} />
                  <Stat label="✅ 신규 임포트" value={result.inserted?.toLocaleString()} highlight />
                  <Stat label="📧 보호 계정 보존" value={result.skipped?.protected} />
                  <Stat label="🔄 이메일 중복 스킵" value={result.skipped?.email_duplicate} />
                  <Stat label="🆔 legacy_id 중복 스킵" value={result.skipped?.legacy_id_duplicate} />
                  <Stat label="📭 이메일 없음 스킵" value={result.skipped?.no_email} />
                </div>
                {result.admins_promoted?.length > 0 && (
                  <div className="mt-4 p-3 bg-white rounded-lg border border-emerald-200">
                    <p className="text-xs font-semibold text-stone-600 mb-1">👑 admin 권한 임포트 ({result.admins_promoted.length}명)</p>
                    <ul className="text-xs text-stone-500 space-y-0.5">
                      {result.admins_promoted.map((e) => <li key={e}>· {e}</li>)}
                    </ul>
                  </div>
                )}
                {result.by_country && (
                  <div className="mt-4 p-3 bg-white rounded-lg border border-emerald-200">
                    <p className="text-xs font-semibold text-stone-600 mb-2">🌏 국가별 분포 (Top 10)</p>
                    <div className="grid grid-cols-2 gap-1 text-xs text-stone-600">
                      {Object.entries(result.by_country)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 10)
                        .map(([c, n]) => (
                          <div key={c} className="flex justify-between">
                            <span>{c}</span>
                            <span className="font-bold">{n.toLocaleString()}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                <p className="mt-4 text-xs text-emerald-700 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> 엔드포인트가 봉인되었습니다. 재실행 불가.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`bg-white rounded-lg border border-stone-200 p-3 ${highlight ? "ring-2 ring-emerald-300" : ""}`}>
      <p className="text-[11px] text-stone-500 uppercase tracking-wide">{label}</p>
      <p className={`text-xl font-bold mt-0.5 ${highlight ? "text-emerald-700" : "text-stone-800"}`}>
        {value ?? 0}
      </p>
    </div>
  );
}
